import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import api from '@/api';

// Only keep federal lookup URL - states will use Gemini
const FEDERAL_LOOKUP_URL = 'https://electorate.aec.gov.au/';

const STATE_NAMES = {
  'QLD': 'Queensland',
  'NSW': 'New South Wales',
  'VIC': 'Victoria',
  'SA': 'South Australia',
  'WA': 'Western Australia',
  'TAS': 'Tasmania',
  'NT': 'Northern Territory',
  'ACT': 'Australian Capital Territory'
};

export default function ElectorateSearch({ 
  campaignId, 
  onSelect, 
  onVerify,
  loading,
  verified,
  theme = {},
  placeholder = "Enter postcode or electorate",
  targetLevel = "federal",  // "federal" or "state"
  targetState = ""  // Empty string means all states
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [addressLooking, setAddressLooking] = useState(false);
  const [addressResult, setAddressResult] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);
  const [hasSelected, setHasSelected] = useState(false);  // Flag to prevent dropdown reopening after selection
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  const t = {
    primary: theme.primary || '#0a2a66',
    primaryText: theme.primaryText || '#ffffff',
    secondary: theme.secondary || '#eef2ff',
    fieldText: theme.fieldText || '#0a0a0a',
    fieldBorder: theme.fieldBorder || '#d1d5db',
    bg: theme.bg || '#ffffff',
    text: theme.text || '#000000'
  };

  const fieldStyle = {
    backgroundColor: t.secondary,
    color: t.fieldText,
    border: `1px solid ${t.fieldBorder}`
  };

  // Handle "Can't find your electorate?" click
  const handleLookupClick = () => {
    if (targetLevel === "federal") {
      // Federal - use AEC iframe
      setShowLookupModal(true);
    } else {
      // State - use Gemini address lookup
      setAddressInput('');
      setAddressResult(null);
      setShowAddressModal(true);
    }
  };

  // Handle Gemini address lookup for state campaigns
  const handleAddressLookup = async () => {
    if (!addressInput || addressInput.length < 5) return;
    
    setAddressLooking(true);
    setAddressResult(null);
    
    try {
      const response = await api.post(`/widget/${campaignId}/lookup-address`, { address: addressInput });
      setAddressResult(response.data);
    } catch (err) {
      setAddressResult({ success: false, message: 'Failed to look up address. Please try again.' });
    }
    
    setAddressLooking(false);
  };

  // Handle selecting the address lookup result
  const handleSelectAddressResult = () => {
    if (addressResult?.success) {
      const selection = {
        type: 'electorate',
        value: addressResult.electorate,
        state: addressResult.state,
        postcode: addressResult.postcode || ''
      };
      setQuery(addressResult.electorate);
      setSelectedValue(selection);
      setHasSelected(true);  // Prevent dropdown from reopening
      setShowDropdown(false);
      onSelect(selection);
      setShowAddressModal(false);
      
      // Auto-verify with postcode if available
      if (addressResult.postcode && onVerify) {
        onVerify({ electorate: addressResult.electorate, postcode: addressResult.postcode });
      }
    }
  };

  // Search as user types
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    // Don't search if user just selected an option
    if (hasSelected) {
      return;
    }
    
    if (!query || query.length < 1) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    // Always show dropdown when typing (for the "can't find" link)
    setShowDropdown(true);

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/widget/${campaignId}/search-location`, { params: { q: query } });
        // Handle both axios response (res.data) and ensure results is an array
        const data = res.data || res;
        const resultsList = data.results || data || [];
        setResults(Array.isArray(resultsList) ? resultsList : []);
      } catch (err) {
        console.error('Search error:', err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, campaignId, hasSelected]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    let selection = null;
    
    if (item.type === 'postcode') {
      setQuery(item.value);
      selection = { type: 'postcode', value: item.value, postcode: item.value };
      setSelectedValue(selection);
      onSelect({ type: 'postcode', postcode: item.value });
    } else if (item.type === 'postcode_match') {
      // Postcode search returned an electorate - use the associated postcode
      setQuery(`${item.label} (${item.state})`);
      selection = { type: 'electorate', value: item.value, state: item.state, postcode: item.postcode };
      setSelectedValue(selection);
      onSelect({ type: 'electorate', electorate: item.value, state: item.state, postcode: item.postcode });
    } else {
      // Electorate selected - use the sample postcode
      setQuery(`${item.label} (${item.sublabel})`);
      selection = { type: 'electorate', value: item.value, state: item.state, postcode: item.postcode };
      setSelectedValue(selection);
      onSelect({ type: 'electorate', electorate: item.value, state: item.state, postcode: item.postcode });
    }
    
    // Close dropdown and mark as selected to prevent reopening
    setShowDropdown(false);
    setHasSelected(true);
    setResults([]);
    
    // Auto-verify immediately after selection
    if (selection) {
      onVerify(selection);
    }
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setSelectedValue(null);
    setHasSelected(false);  // Allow searching again when user types
    onSelect(null); // Reset selection when typing
  };

  return (
    <div className="relative">
      {/* Label above the search field */}
      <div className="text-sm font-medium mb-2" style={{ color: t.text }}>
        Find your electorate
      </div>
      <div className="relative">
        <Input
          ref={inputRef}
          data-testid="electorate-search-input"
          placeholder="e.g. 2000 / Perth / Anthony Albanese"
          value={query}
          onChange={handleInputChange}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          style={fieldStyle}
          autoComplete="off"
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
        )}
        {loading && !searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-green-500 rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {/* Dropdown - always show when typing (for "can't find" link) */}
      {showDropdown && query.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto"
          style={{ borderColor: t.fieldBorder }}
        >
          {results.length > 0 ? (
            results.map((item, idx) => (
              <button
                key={idx}
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-gray-100 border-b last:border-b-0 transition-colors"
                onClick={() => handleSelect(item)}
                data-testid={`search-result-${idx}`}
              >
                <div className="font-medium text-sm" style={{ color: t.text }}>
                  {item.type === 'postcode' ? `📍 ${item.label}` : 
                   item.type === 'mp' ? `👤 ${item.label}` : item.label}
                </div>
                <div className="text-xs text-gray-500">
                  {item.sublabel}
                </div>
              </button>
            ))
          ) : !searching ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No results found for &quot;{query}&quot;
            </div>
          ) : null}
          
          {/* Can't find electorate link - always visible */}
          <button
            type="button"
            className="w-full px-3 py-3 text-left hover:bg-blue-50 border-t transition-colors"
            onClick={handleLookupClick}
            data-testid="electorate-lookup-link"
          >
            <div className="text-sm text-blue-600 font-medium">
              Can&apos;t find your electorate? Look it up here →
            </div>
          </button>
        </div>
      )}

      {/* Address Lookup Modal - for state campaigns using Gemini */}
      {showAddressModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAddressModal(false)}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Find Your Electorate by Address</h3>
                <p className="text-sm text-gray-500">
                  Enter your street address to find your {targetState ? STATE_NAMES[targetState] : ''} state electorate
                </p>
              </div>
              <button 
                onClick={() => setShowAddressModal(false)}
                className="p-1 hover:opacity-80 text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4">
              {!addressResult?.success ? (
                <>
                  <Input
                    placeholder="e.g. 123 Main Street, Suburb"
                    value={addressInput}
                    onChange={(e) => setAddressInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddressLookup()}
                    className="w-full"
                  />
                  <Button
                    onClick={handleAddressLookup}
                    disabled={addressLooking || addressInput.length < 5}
                    className="w-full"
                    style={{ backgroundColor: t.primary, color: t.primaryText }}
                  >
                    {addressLooking ? 'Looking up...' : 'Find Electorate'}
                  </Button>
                  {addressResult && !addressResult.success && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                      {addressResult.message}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 text-center">
                    Powered by AI • Results may vary
                  </p>
                </>
              ) : (
                <div className="text-center space-y-4">
                  <div className="text-green-600 font-semibold text-lg">Electorate Found!</div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold" style={{ color: t.primary }}>{addressResult.electorate}</div>
                    {addressResult.mp_name && (
                      <div className="text-sm text-gray-600 mt-2">
                        MP: {addressResult.mp_name} ({addressResult.mp_party})
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setAddressResult(null)}
                      className="flex-1"
                    >
                      Try Another
                    </Button>
                    <Button
                      onClick={handleSelectAddressResult}
                      className="flex-1"
                      style={{ backgroundColor: t.primary, color: t.primaryText }}
                    >
                      Use This Electorate
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Federal Electorate Lookup Modal - AEC iframe */}
      {showLookupModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
          onClick={(e) => e.target === e.currentTarget && setShowLookupModal(false)}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between bg-white">
              <div>
                <h3 className="font-semibold text-gray-900">
                  Find Your Federal Electorate
                </h3>
                <p className="text-sm text-gray-500">
                  Use the AEC tool below to find your electorate
                </p>
              </div>
              <button 
                onClick={() => setShowLookupModal(false)}
                className="p-1 hover:opacity-80 text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={FEDERAL_LOOKUP_URL}
                className="w-full h-full min-h-[400px]"
                title="Electorate Finder"
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
            </div>
            <div className="p-3 border-t bg-gray-50 text-center">
              <p className="text-sm text-gray-600">
                Once you find your electorate, close this and type it in the search box above.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
